import React, { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import api, { apiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { PublicHeader, PublicFooter, DisclaimerNotice } from "./PublicLayout";
import { InlineSpinner } from "@/components/common/Loading";
import { CheckCircle2, AlertCircle, Building2, Users, UserSearch, HeartHandshake, HelpCircle } from "lucide-react";

const TYPE_META = {
  agency_owner: { icon: Building2, desc: "Buy an agency or build one from the ground up." },
  work_in_agency: { icon: Users, desc: "Sales, service, operations and producer careers." },
  agency_recruiter: { icon: UserSearch, desc: "Apply to recruit for agencies (admin-approved)." },
  referral_partner: { icon: HeartHandshake, desc: "Refer candidates as an individual or organization." },
  other: { icon: HelpCircle, desc: "Tell us what you're interested in." },
};
const INTEREST_TO_TYPE = { buy_agency: "agency_owner", start_agency: "agency_owner", work_in_agency: "work_in_agency", other: "other" };
const INTEREST_TO_PATH = { buy_agency: "buy", start_agency: "start" };

const BLANK = {
  first_name: "", last_name: "", address: "", city: "", state: "", zip_code: "",
  mobile: "", email: "", password: "", confirm: "", source: "", referral_source_name: "",
  referral_source_other: "", accept_privacy: false, accept_terms: false, recruiting_opt_in: false,
  requested_account_type: "", agency_owner_interest: "", position_interest: "", license_status: "",
  current_company: "", current_role: "", experience: "", open_to_relocation: "", preferred_location: "",
  primary_markets: "", linkedin_url: "", referral_type: "", company_name: "", referral_candidates: "",
  other_interest_details: "",
};

const orderedSources = (sources) => {
  const arr = [...(sources || [])];
  const rest = arr.filter((s) => s.toLowerCase() !== "other");
  const other = arr.filter((s) => s.toLowerCase() === "other");
  return [...rest, ...other];
};

export default function Register() {
  const [params] = useSearchParams();
  const [info, setInfo] = useState(null);
  const preInterest = params.get("interest") || "";
  const [f, setF] = useState({
    ...BLANK,
    requested_account_type: INTEREST_TO_TYPE[preInterest] || "",
    agency_owner_interest: INTEREST_TO_PATH[preInterest] || "",
    ref_code: params.get("ref") || "",
  });
  const [refInfo, setRefInfo] = useState(null);
  useEffect(() => {
    const c = (f.ref_code || "").trim();
    if (!c) { setRefInfo(null); return; }
    const t = setTimeout(() => { api.get(`/public/referral/${encodeURIComponent(c)}`).then((r) => setRefInfo(r.data)).catch(() => setRefInfo({ valid: false })); }, 400);
    return () => clearTimeout(t);
  }, [f.ref_code]);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  useEffect(() => { api.get("/public/site-info").then((r) => setInfo(r.data)).catch(() => {}); }, []);
  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));
  const type = f.requested_account_type;

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (!type) return setError("Please choose the type of account you are creating.");
    if (f.password.length < 8) return setError("Password must be at least 8 characters.");
    if (f.password !== f.confirm) return setError("Passwords do not match.");
    if (!(f.accept_privacy && f.accept_terms)) return setError("Please acknowledge the Privacy Notice and Terms.");
    setLoading(true);
    try {
      const { confirm, ...payload } = f;
      await api.post("/public/register", payload);
      setDone(true);
    } catch (err) { setError(apiError(err)); }
    finally { setLoading(false); }
  };

  if (done) {
    return (
      <div className="min-h-screen bg-slate-50 flex flex-col">
        <PublicHeader />
        <main className="flex-1 flex items-center justify-center px-6 py-16">
          <div className="max-w-md w-full bg-white border border-slate-200 rounded-2xl p-8 text-center" data-testid="register-success">
            <CheckCircle2 className="w-12 h-12 text-emerald-500 mx-auto mb-4" />
            <h1 className="font-display text-2xl font-bold text-slate-900">Check your email</h1>
            <p className="mt-2 text-sm text-slate-600">
              We've sent a verification link to <strong>{f.email}</strong>. Please verify your email so our
              team can review your request. Portal access is granted after verification and approval.
            </p>
            <Link to="/login"><Button className="mt-6 bg-[#006090] hover:bg-[#004E75]" data-testid="register-to-login">Go to Sign In</Button></Link>
          </div>
        </main>
        <PublicFooter disclaimer={info?.disclaimer} />
      </div>
    );
  }

  const types = info?.account_types || [];

  return (
    <div className="min-h-screen bg-slate-50 flex flex-col">
      <PublicHeader />
      <main className="flex-1 max-w-3xl w-full mx-auto px-6 py-12">
        <h1 className="font-display text-3xl font-bold text-slate-900" data-testid="register-title">Create My Account</h1>
        <p className="mt-1.5 text-sm text-slate-600">Start by telling us the type of account you're creating. Your selection is a request — our team reviews and approves accounts before portal access is granted.</p>
        {refInfo?.valid && <div className="mt-4 rounded-lg border border-emerald-200 bg-emerald-50 px-4 py-2.5 text-sm text-emerald-800" data-testid="register-referral-banner">You were referred by <strong>{refInfo.partner_first_name || "a Referral Partner"}</strong>. Your referral will be recorded automatically.</div>}

        {/* Step 1: Account type */}
        <div className="mt-8 rounded-xl border border-slate-200 bg-white p-6">
          <Label className="text-sm font-semibold text-slate-800">What type of account are you creating? <span className="text-rose-500">*</span></Label>
          <div className="mt-3 grid sm:grid-cols-2 gap-3" data-testid="account-type-grid">
            {types.map((t) => {
              const M = TYPE_META[t.key] || {};
              const Icon = M.icon || HelpCircle;
              const active = type === t.key;
              return (
                <button type="button" key={t.key} onClick={() => set("requested_account_type", t.key)} data-testid={`acct-type-${t.key}`}
                  className={`text-left rounded-xl border p-4 transition-all ${active ? "border-[#0D9488] bg-[#0D9488]/5 ring-1 ring-[#0D9488]" : "border-slate-200 hover:border-slate-300"}`}>
                  <span className={`w-9 h-9 rounded-lg flex items-center justify-center ${active ? "bg-[#0D9488] text-white" : "bg-slate-100 text-[#006090]"}`}><Icon className="w-5 h-5" /></span>
                  <p className="mt-2.5 font-semibold text-slate-900 text-sm">{t.label}</p>
                  <p className="text-xs text-slate-500 mt-0.5">{M.desc}</p>
                </button>
              );
            })}
          </div>
        </div>

        {/* Steps 2+: only after a type is chosen */}
        {type && (
          <form onSubmit={submit} className="mt-6 space-y-6" data-testid="register-form">
            {error && (
              <div data-testid="register-error" className="flex items-start gap-2 rounded-md bg-rose-50 border border-rose-200 px-3 py-2 text-sm text-rose-700">
                <AlertCircle className="w-4 h-4 mt-0.5 shrink-0" /><span>{error}</span>
              </div>
            )}

            <div className="rounded-xl border border-slate-200 bg-white p-6 grid sm:grid-cols-2 gap-4">
              <Field label="First Name" req><Input value={f.first_name} onChange={(e) => set("first_name", e.target.value)} required data-testid="reg-first_name" /></Field>
              <Field label="Last Name" req><Input value={f.last_name} onChange={(e) => set("last_name", e.target.value)} required data-testid="reg-last_name" /></Field>
              <Field label="Home Street Address" req className="sm:col-span-2"><Input value={f.address} onChange={(e) => set("address", e.target.value)} required data-testid="reg-address" /></Field>
              <Field label="City" req><Input value={f.city} onChange={(e) => set("city", e.target.value)} required data-testid="reg-city" /></Field>
              <div className="grid grid-cols-2 gap-4">
                <Field label="State" req><Input value={f.state} onChange={(e) => set("state", e.target.value.toUpperCase())} maxLength={2} placeholder="TX" required data-testid="reg-state" /></Field>
                <Field label="ZIP Code" req><Input value={f.zip_code} onChange={(e) => set("zip_code", e.target.value)} required data-testid="reg-zip_code" /></Field>
              </div>
              <Field label="Mobile Phone" req><Input value={f.mobile} onChange={(e) => set("mobile", e.target.value)} required data-testid="reg-mobile" /></Field>
              <Field label="Email Address" req><Input type="email" value={f.email} onChange={(e) => set("email", e.target.value)} required data-testid="reg-email" /></Field>
              <Field label="Password" req><Input type="password" value={f.password} onChange={(e) => set("password", e.target.value)} required data-testid="reg-password" /></Field>
              <Field label="Confirm Password" req><Input type="password" value={f.confirm} onChange={(e) => set("confirm", e.target.value)} required data-testid="reg-password_confirm" /></Field>
            </div>

            {/* Dynamic questions per requested account type */}
            <DynamicSection type={type} f={f} set={set} />

            {/* Source + referral name */}
            <div className="rounded-xl border border-slate-200 bg-white p-6 grid sm:grid-cols-2 gap-4">
              <Field label="How did you hear about us?" className="sm:col-span-2">
                <Select value={f.source} onValueChange={(v) => set("source", v)}>
                  <SelectTrigger data-testid="reg-source"><SelectValue placeholder="Select a source" /></SelectTrigger>
                  <SelectContent className="bg-white max-h-72">
                    {orderedSources(info?.sources).map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                  </SelectContent>
                </Select>
              </Field>
              {f.source && f.source.toLowerCase() === "other" && (
                <Field label="Please specify" className="sm:col-span-2"><Input value={f.referral_source_other} onChange={(e) => set("referral_source_other", e.target.value)} data-testid="reg-source-other" /></Field>
              )}
              <Field label="Person or Company Name" className="sm:col-span-2">
                <Input value={f.referral_source_name} onChange={(e) => set("referral_source_name", e.target.value)} placeholder="e.g. Jane Smith, ABC Recruiting, Local Chamber of Commerce" data-testid="reg-referral-name" />
                <p className="mt-1 text-xs text-slate-400">If someone or an organization referred you, please enter their name.</p>
              </Field>
              <Field label="Tell us more about how you heard about us (optional)" className="sm:col-span-2">
                <Input value={f.how_heard || ""} onChange={(e) => set("how_heard", e.target.value)} maxLength={300} data-testid="reg-how-heard" />
              </Field>
              <Field label="Referral code (optional)" className="sm:col-span-2">
                <Input value={f.ref_code} onChange={(e) => set("ref_code", e.target.value.trim())} data-testid="reg-ref-code" />
                {f.ref_code && refInfo && !refInfo.valid && <p className="mt-1 text-xs text-amber-600" data-testid="reg-ref-invalid">We couldn't find that referral code. You can still register.</p>}
              </Field>
            </div>

            <div className="rounded-xl border border-slate-200 bg-white p-6 space-y-3">
              <Consent testId="reg-accept-privacy" checked={f.accept_privacy} onChange={(v) => set("accept_privacy", v)}>
                I acknowledge the Privacy Notice and understand my information is used to evaluate and respond to my inquiry.
              </Consent>
              <Consent testId="reg-accept-terms" checked={f.accept_terms} onChange={(v) => set("accept_terms", v)}>
                I agree to the Terms and understand account/security emails (verification, password reset, security notices) are required.
              </Consent>
              <Consent testId="reg-recruiting-opt-in" checked={f.recruiting_opt_in} onChange={(v) => set("recruiting_opt_in", v)}>
                (Optional) I'd like to receive recruiting communications about relevant opportunities. Opt-out requests are honored.
              </Consent>
            </div>

            <DisclaimerNotice disclaimer={info?.disclaimer} />

            <div className="flex items-center justify-between">
              <Link to="/login" className="text-sm text-[#0093D6] font-medium hover:underline">Already have an account? Sign in</Link>
              <Button type="submit" disabled={loading} data-testid="register-submit" className="bg-[#0D9488] hover:bg-[#0b7d72] rounded-full px-8 h-11">
                {loading ? <InlineSpinner /> : "Create My Account"}
              </Button>
            </div>
          </form>
        )}
      </main>
      <PublicFooter disclaimer={info?.disclaimer} />
    </div>
  );
}

const LICENSE = [["yes", "Yes"], ["no", "No"], ["in_progress", "In Progress"]];
const EXPERIENCE = [["0-1", "Less than 1 year"], ["1-3", "1–3 years"], ["3-5", "3–5 years"], ["5-10", "5–10 years"], ["10+", "10+ years"]];

function DynamicSection({ type, f, set }) {
  if (type === "agency_owner") {
    return (
      <SectionCard title="Agency ownership — a few quick questions" testId="dyn-agency_owner">
        <PickField label="Which path interests you most?" value={f.agency_owner_interest} onChange={(v) => set("agency_owner_interest", v)}
          options={[["buy", "Buy an Existing Agency"], ["start", "Start an Agency from Scratch"], ["unsure", "I'm Not Sure Yet"]]} testId="dyn-owner-path" />
        <SelectField label="Do you currently hold an insurance license?" value={f.license_status} onChange={(v) => set("license_status", v)} options={LICENSE} testId="dyn-owner-license" />
        <GridInput label="Current Occupation / Role" value={f.current_role} onChange={(v) => set("current_role", v)} testId="dyn-owner-role" />
        <SelectField label="Years of sales / business / insurance experience" value={f.experience} onChange={(v) => set("experience", v)} options={EXPERIENCE} testId="dyn-owner-exp" />
        <SelectField label="Open to relocating for the right opportunity?" value={f.open_to_relocation} onChange={(v) => set("open_to_relocation", v)} options={[["yes", "Yes"], ["no", "No"], ["maybe", "Maybe"]]} testId="dyn-owner-relocate" />
      </SectionCard>
    );
  }
  if (type === "work_in_agency") {
    return (
      <SectionCard title="Working in an agency — a few quick questions" testId="dyn-work_in_agency">
        <SelectField label="What type of position interests you?" value={f.position_interest} onChange={(v) => set("position_interest", v)}
          options={[["sales_producer", "Sales / Producer"], ["customer_service", "Customer Service"], ["operations", "Office / Operations"], ["marketing", "Marketing"], ["licensed_professional", "Licensed Insurance Professional"], ["other", "Other"], ["not_sure", "Not Sure"]]} testId="dyn-work-position" />
        <SelectField label="Do you currently hold an insurance license?" value={f.license_status} onChange={(v) => set("license_status", v)} options={LICENSE} testId="dyn-work-license" />
        <SelectField label="Insurance / sales experience" value={f.experience} onChange={(v) => set("experience", v)} options={EXPERIENCE} testId="dyn-work-exp" />
        <GridInput label="Preferred work location" value={f.preferred_location} onChange={(v) => set("preferred_location", v)} testId="dyn-work-location" />
      </SectionCard>
    );
  }
  if (type === "agency_recruiter") {
    return (
      <SectionCard title="Recruiter application" testId="dyn-agency_recruiter">
        <p className="sm:col-span-2 -mt-1 text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-md px-3 py-2">Submitting this does not grant recruiter access. An administrator reviews and assigns authorized roles after approval.</p>
        <GridInput label="Current Company / Organization" value={f.current_company} onChange={(v) => set("current_company", v)} testId="dyn-rec-company" />
        <GridInput label="Current Role / Title" value={f.current_role} onChange={(v) => set("current_role", v)} testId="dyn-rec-role" />
        <SelectField label="Recruiting experience" value={f.experience} onChange={(v) => set("experience", v)} options={EXPERIENCE} testId="dyn-rec-exp" />
        <GridInput label="Insurance industry experience" value={f.primary_markets} onChange={(v) => set("primary_markets", v)} testId="dyn-rec-industry" placeholder="e.g. 4 years P&C" />
        <GridInput label="Primary markets / geography" value={f.preferred_location} onChange={(v) => set("preferred_location", v)} testId="dyn-rec-markets" />
        <GridInput label="LinkedIn URL (optional)" value={f.linkedin_url} onChange={(v) => set("linkedin_url", v)} testId="dyn-rec-linkedin" />
      </SectionCard>
    );
  }
  if (type === "referral_partner") {
    return (
      <SectionCard title="Referral partner application" testId="dyn-referral_partner">
        <p className="sm:col-span-2 -mt-1 text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-md px-3 py-2">Submitting this does not grant portal access. An administrator reviews and approves referral partners.</p>
        <PickField label="Are you registering as:" value={f.referral_type} onChange={(v) => set("referral_type", v)} options={[["individual", "Individual"], ["company", "Company / Organization"]]} testId="dyn-ref-type" />
        {f.referral_type === "company" && <GridInput label="Company / Organization Name" value={f.company_name} onChange={(v) => set("company_name", v)} testId="dyn-ref-company" />}
        <GridInput label="Current Role / Title" value={f.current_role} onChange={(v) => set("current_role", v)} testId="dyn-ref-role" />
        <GridInput label="What candidates do you expect to refer? (optional)" value={f.referral_candidates} onChange={(v) => set("referral_candidates", v)} testId="dyn-ref-candidates" />
        <GridInput label="Primary geographic market (optional)" value={f.preferred_location} onChange={(v) => set("preferred_location", v)} testId="dyn-ref-market" />
        <GridInput label="Website / LinkedIn (optional)" value={f.linkedin_url} onChange={(v) => set("linkedin_url", v)} testId="dyn-ref-linkedin" />
      </SectionCard>
    );
  }
  return (
    <SectionCard title="Tell us more" testId="dyn-other">
      <div className="sm:col-span-2">
        <Label className="text-xs font-semibold text-slate-600">Please tell us what you're interested in</Label>
        <Textarea className="mt-1.5" value={f.other_interest_details} onChange={(e) => set("other_interest_details", e.target.value)} data-testid="dyn-other-details" />
      </div>
    </SectionCard>
  );
}

function SectionCard({ title, children, testId }) {
  return (
    <div className="rounded-xl border border-slate-200 bg-white p-6" data-testid={testId}>
      <h3 className="text-sm font-semibold text-slate-800 mb-3">{title}</h3>
      <div className="grid sm:grid-cols-2 gap-4">{children}</div>
    </div>
  );
}

function GridInput({ label, value, onChange, testId, placeholder }) {
  return <Field label={label}><Input value={value} onChange={(e) => onChange(e.target.value)} placeholder={placeholder} data-testid={testId} /></Field>;
}

function SelectField({ label, value, onChange, options, testId }) {
  return (
    <Field label={label}>
      <Select value={value} onValueChange={onChange}>
        <SelectTrigger data-testid={testId}><SelectValue placeholder="Select…" /></SelectTrigger>
        <SelectContent className="bg-white">{options.map(([k, l]) => <SelectItem key={k} value={k}>{l}</SelectItem>)}</SelectContent>
      </Select>
    </Field>
  );
}

function PickField({ label, value, onChange, options, testId }) {
  return (
    <div className="sm:col-span-2">
      <Label className="text-xs font-semibold text-slate-600">{label}</Label>
      <div className="mt-1.5 flex flex-wrap gap-2" data-testid={testId}>
        {options.map(([k, l]) => (
          <button type="button" key={k} onClick={() => onChange(k)}
            className={`rounded-lg border px-3.5 py-2 text-sm transition-colors ${value === k ? "border-[#0D9488] bg-[#0D9488]/5 ring-1 ring-[#0D9488]" : "border-slate-200 hover:border-slate-300"}`}>
            {l}
          </button>
        ))}
      </div>
    </div>
  );
}

function Field({ label, req, children, className = "" }) {
  return (
    <div className={className}>
      <Label className="text-xs font-semibold text-slate-600">{label}{req && <span className="text-rose-500"> *</span>}</Label>
      <div className="mt-1.5">{children}</div>
    </div>
  );
}

function Consent({ checked, onChange, children, testId }) {
  return (
    <label className="flex items-start gap-3 text-sm text-slate-700 cursor-pointer">
      <Checkbox checked={checked} onCheckedChange={onChange} data-testid={testId} className="mt-0.5" />
      <span>{children}</span>
    </label>
  );
}
